import {globalUser} from "./globals.js";
import {getCurrentGame, objectInArray} from "./helpers.js";
import {db, Companies, Brands, CompanyLogs} from "../models/models.js";
import {getGameCurrentUserCompany, saveGameDataToLocalStorage} from "./games.js";
import {
    getActiveCompetitorsForCompany,
    getCompanyLatestEvent,
    getCompanyParentEvents,
    getCompanyRosterByType, updateObjectOverness
} from "./companies.js";
import {
    addRosterBrands,
    getRosterBrands,
    getRosterSuperstars, getRosterTagTeams,
    removeRosterBrands
} from "../models/roster.js";
import {addCompanyCompanyLogs, getCompanyChampionships} from "../models/company.js";
import {checkAreChampionshipsEqual, compareChampionships, getUniqueChampionships} from "./championships.js";
import {getSuperstarChampionshipsFromCompanyChampionships} from "./superstars.js";
import {CompanyLog} from "../models/companyLog.js";
import {Brand} from "../models/brand.js";
import {checkTalentRequestAction} from "./talentRequests.js";
import {createBrandDraft, getDraftData} from "./draft.js";

async function getViewBrandsData(companyUid) {  // get_company_brands_view
    const currentGame = await getCurrentGame();

    // Get selected company
    let selectedCompany;
    if (companyUid) {
        selectedCompany = await Companies.get(companyUid)
    }
    else {
        selectedCompany = await getGameCurrentUserCompany(currentGame)
    }

    // Get main roster's superstars, company championships and parent events
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const mainRosterSuperstars = await getRosterSuperstars(mainRoster);
    const companyChampionships = await getCompanyChampionships(selectedCompany);
    const parentEvents = await getCompanyParentEvents(selectedCompany);

    // Get brands data
    const rosterBrands = await getRosterBrands(mainRoster);
    return rosterBrands.map(x => getBrandData(x, mainRosterSuperstars, parentEvents, companyChampionships));
}

function getBrandData(brand, rosterSuperstars, parentEvents, companyChampionships) {  // get_brand_data
    let data = {};

    // Get attributes
    data["name"] = brand.name;
    data["uid"] = brand.$loki;
    data["weeklyShowName"] = brand.weeklyShowName;
    data["weeklyShowIsExclusive"] = brand.weeklyShowIsExclusive;
    data["color"] = brand.color;
    data["overness"] = brand.overness;
    data["hot"] = brand.hot;
    data["stale"] = brand.stale;

    // Get brand objects
    const brandSuperstars = rosterSuperstars.filter(x => x.brandName === brand.name);
    brandSuperstars.sort((a, b) => b.popularity - a.popularity);
    const brandChampionships = getUniqueChampionships(companyChampionships.filter(x => x.brandName === brand.name));
    brandChampionships.sort(compareChampionships);
    const brandEvents = parentEvents.filter(x => x.brandName === brand.name);

    // Get data for brand objects
    data["superstars"] = brandSuperstars.map(x => ({"name": x.name}));
    data["championships"] = brandChampionships.map(x => ({"name": x.name, "prestige": x.prestige}));
    data["events"] = brandEvents.map(x => ({"name": x.name, "prestige": x.prestige}));

    return data;
}

function getBrandColorForEvent(event, rosterBrands) {  // Previously get_brand_color_for_event
    const brand = getBrandForEvent(event, rosterBrands);
    if (brand) {
        return brand.color
    }
    return null;
}

function getBrandColorFromBrandName(brandName, rosterBrands) {  // Previously get_brand_color_from_brand_name
    const brand = rosterBrands.find(x => x.name === brandName);
    if (brand) {
        return brand.color
    }
    return null;
}

function getBrandForEvent(event, rosterBrands) {  // Previously get_brand_for_event
    if (event.weeklyShow) {
        return rosterBrands.find(x => x.weeklyShowName === event.name)
    }
    else {
        return rosterBrands.find(x => x.name === event.brandName)
    }
}

function getBrandIndexForWeeklyShow(event, rosterBrands) {  // Previously get_brand_index_for_weekly_show
    // Get index of current event (weekly show) using weekly show names of roster brands
    const brandIndex = null;
    const brandWeeklyShowNames = rosterBrands.map(x => x.weeklyShowName);
    if (brandWeeklyShowNames.indexOf(event.name) >= 0) {
        return brandWeeklyShowNames.indexOf(event.name)
    }
    return brandIndex;
}

function setObjectBrand(brandObject, brandName) {  // Previously set_object_brand
    // Set brand name for superstars, championships and events
    if (['superstar', 'championship', 'event'].indexOf(brandObject.objectName) >= 0) {
        brandObject.brandName = brandName;
    }
}

function removeObjectBrand(brandObject) {  // Previously remove_object_brand
    // Remove brand name for superstars, championships and events
    if (['superstar', 'championship', 'event'].indexOf(brandObject.objectName) >= 0) {
        brandObject.brandName = null;
    }
}

async function getAddBrandSelections() {  // get_add_brand_selections_view
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const companyChampionships = await getCompanyChampionships(selectedCompany);
    const parentEvents = await getCompanyParentEvents(selectedCompany);
    const rosterBrands = await getRosterBrands(mainRoster);
    const ranks = {'Main Eventer': 4, 'Upper Midcarder': 3, 'Lower Midcarder': 2, 'Jabroni': 1};

    // Get superstars
    data['superstars'] = rosterSuperstars.map(x => {
        const brandColor = getBrandColorFromBrandName(x.brandName, rosterBrands);
        const championships = getSuperstarChampionshipsFromCompanyChampionships(x, companyChampionships);
        const championshipNames = championships.map(y => y.name);
        return {
            'name': x.name,
            'popularity': x.popularity,
            'age': x.age,
            'retired': x.retired,
            'brandName': x.brandName,
            'uid': x.$loki,
            'appearances': x.partTimer ? x.appearances : null,
            'injured': x.injured > 0 ? x.injured : false,
            'fullTimeManager': x.fullTimeManager,
            'championshipNames': championshipNames,
            'brandColor': brandColor
        }
    });
    data['superstars'].sort((a, b) => b.popularity - a.popularity);

    // Get championships
    const uniqueChampionships = getUniqueChampionships(companyChampionships);
    data['championships'] = uniqueChampionships.map(x => {
        const brandColor = getBrandColorFromBrandName(x.brandName, rosterBrands);
        return {
            'name': x.name,
            'rank': x.rank,
            'prestige': x.prestige,
            'brandName': x.brandName,
            'uid': x.$loki,
            'brandColor': brandColor
        }
    });
    data['championships'].sort((a, b) => ranks[b.rank] - ranks[a.rank]);

    // Get events
    data['events'] = parentEvents.map(x => {
        const brandColor = getBrandColorFromBrandName(x.brandName, rosterBrands);
        return {
            'name': x.name,
            'prestige': x.prestige,
            'brandName': x.brandName,
            'uid': x.$loki,
            'brandColor': brandColor
        }
    });
    data['events'].sort((a, b) => ranks[b.rank] - ranks[a.rank]);

    return data;
}

async function addOrEditBrand(brandName, weeklyShowName, weeklyShowIsExclusive, brandColor, superstarUids,
        eventUids, championshipUids, edit, brandUid) {  // add_brand_view
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const companyChampionships = await getCompanyChampionships(selectedCompany);
    const parentEvents = await getCompanyParentEvents(selectedCompany);
    const currentEvent = await getCompanyLatestEvent(selectedCompany);
    const competitors = await getActiveCompetitorsForCompany(selectedCompany, currentGame);
    let rosterBrands = await getRosterBrands(mainRoster);
    const competitorMainRosters = (await Promise.all(competitors.map(x => getCompanyRosterByType(x, 'main')))).flat();
    const competitorBrands = (await Promise.all(competitorMainRosters.map(x => getRosterBrands(x)))).flat();
    const maxBrands = 3;

    // Get cost
    let cost = 50;
    if (!edit) {
        if (rosterBrands.length > 0) {
            cost = 100
        }
        // No cost for first brand
        else {
            cost = 0
        }
    }

    // Check paid
    if (!globalUser.paid) {
        data['error'] = 'not-paid';
        return data;
    }

    // Check points
    if (selectedCompany.points < cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Check for story
    if (['mars', 'passion'].indexOf(selectedCompany.storyName) >= 0) {
        data['error'] = selectedCompany.storyName;
        return data;
    }

    // Check for max brands
    if (!edit && rosterBrands.length + 1 > maxBrands) {
        data['error'] = 'max-brands';
        return data;
    }

    // Get brand if editing
    let brand;
    if (edit) {
        brand = await Brands.get(brandUid);
        if (!brand) {
            data['error'] = 'brand-not-found';
            return data;
        }
    }

    // Check brand name uniqueness
    let brandNamesToCheck = rosterBrands.concat(competitorBrands).map(x => x.name);
    if (edit) {
        brandNamesToCheck = brandNamesToCheck.filter(x => x !== brand.name);
    }
    if (brandNamesToCheck.indexOf(brandName) >= 0) {
        data['error'] = 'brand-name-exists';
        return data;
    }

    // Check weekly show name uniqueness (against all brands and competitors' weekly shows)
    weeklyShowName = weeklyShowName ? weeklyShowName : brandName;
    let weeklyShowNamesToCheck = rosterBrands.concat(competitorBrands).map(x => x.weeklyShowName);
    weeklyShowNamesToCheck.push(...competitors.filter(x => x.weeklyShowName).map(x => x.weeklyShowName));
    if (edit) {
        weeklyShowNamesToCheck = weeklyShowNamesToCheck.filter(x => x !== brand.weeklyShowName);
    }
    if (weeklyShowNamesToCheck.indexOf(weeklyShowName) >= 0) {
        data['error'] = 'weekly-show-name-exists';
        return data;
    }

    // Get brand objects
    const brandSuperstars = getBrandObjectsFromUids(superstarUids, rosterSuperstars);
    const brandEvents = getBrandObjectsFromUids(eventUids, parentEvents);
    let brandChampionships = getBrandObjectsFromUids(championshipUids, companyChampionships);
    brandChampionships = companyChampionships.filter(x => brandChampionships.some(y => checkAreChampionshipsEqual(x, y)));

    brandColor = brandColor ? brandColor : null;
    if (edit) {
        // Edit brand
        brand = await editBrand(brand, brandName, weeklyShowName, weeklyShowIsExclusive, brandColor, rosterSuperstars,
            parentEvents, companyChampionships, brandSuperstars, brandEvents, brandChampionships, currentEvent,
            selectedCompany, currentGame)
    }
    else {
        // Create brand
        brand = await createBrand(brandName, weeklyShowName, weeklyShowIsExclusive, brandColor, brandSuperstars,
            brandEvents, brandChampionships, currentEvent, mainRoster);

        // Add company log
        const newCompanyLog = CompanyLog(currentGame.monthCount, selectedCompany.realYear);
        CompanyLogs.insert(newCompanyLog);
        newCompanyLog.text = brand.name;
        newCompanyLog.type = 'brand-created';
        newCompanyLog.realYear = currentGame.year;
        await addCompanyCompanyLogs(selectedCompany, [newCompanyLog]);
    }

    // Remove points
    selectedCompany.points -= cost;

    db.saveDatabase();

    // Get data to return
    data['points'] = selectedCompany.points;
    data['brandName'] = brand.name;
    data['currentEvent'] = {};
    data['currentEvent']['name'] = currentEvent.name;
    data['currentEvent']['brandName'] = currentEvent.brandName;
    data['currentEvent']['weeklyShow'] = currentEvent.weeklyShow;
    rosterBrands = await getRosterBrands(mainRoster);
    data['currentEvent']['brandColor'] = getBrandColorForEvent(currentEvent, rosterBrands);

    return data;
}

function getBrandObjectsFromUids(uids, objects) {
    let brandObjects = [];
    for (const uid of uids) {
        const brandObject = objects.find(x => x.$loki === uid);
        if (brandObject) {
            brandObjects.push(brandObject)
        }
    }
    return brandObjects;
}

async function createBrand(name, weeklyShowName, weeklyShowIsExclusive, color, superstars, events,
            championships, currentEvent, roster) {  // create_brand
    const brand = Brand();
    brand.name = name;
    brand.weeklyShowName = weeklyShowName;
    brand.weeklyShowIsExclusive = weeklyShowIsExclusive;
    brand.color = color;
    Brands.insert(brand);

    // Add brand name to objects
    for (const x of [...superstars, ...events, ...championships]) {
        setObjectBrand(x, name)
    }

    // Add brand name to current monthly event if part of brand events
    if (!currentEvent.weeklyShow) {
        if (events.map(x => x.name).indexOf(currentEvent.name) >= 0) {
            setObjectBrand(currentEvent, name)
        }
    }
    // If at brand's weekly show, update its name and brandName
    else {
        if (currentEvent.name === weeklyShowName) {
            currentEvent.name = weeklyShowName;
            if (weeklyShowIsExclusive) {
                setObjectBrand(currentEvent, name)
            }
        }
    }

    // Add to roster
    await addRosterBrands(roster, [brand]);

    return brand;
}

async function editBrand(brand, name, weeklyShowName, weeklyShowIsExclusive, color, rosterSuperstars,
            parentEvents, companyChampionships, brandSuperstars, brandEvents, brandChampionships, currentEvent,
            company, currentGame) {  // edit_brand

    // Set/remove brand name from objects
    for (const x of [...rosterSuperstars, ...parentEvents, ...companyChampionships]) {
        if (x.objectName === 'superstar' && objectInArray(x, brandSuperstars) ||
            x.objectName === 'event' && objectInArray(x, brandEvents) ||
            x.objectName === 'championship' && objectInArray(x, brandChampionships)) {
            setObjectBrand(x, name);
            if (objectInArray(x, brandSuperstars)) {
                // Check talent request
                let requestData = {'brandId': brand.$loki};
                await checkTalentRequestAction({company: company, requestType: 'change_brand', talent: x, data: requestData});
            }
        }
        else {
            if (x.brandName === brand.name) {
                removeObjectBrand(x)
            }
        }
    }

    // Add brand name to current monthly event if part of brand events
    if (!currentEvent.weeklyShow) {
        if (brandEvents.map(x => x.name).indexOf(currentEvent.name) >= 0) {
            setObjectBrand(currentEvent, name)
        }
        // Remove brand name from current event if unselected
        else {
            if (currentEvent.brandName === brand.name) {
                removeObjectBrand(currentEvent)
            }
        }
    }
    // If at brand's weekly show, update its name and brandName
    else {
        if (currentEvent.name === brand.weeklyShowName) {
            currentEvent.name = weeklyShowName;
            if (weeklyShowIsExclusive) {
                setObjectBrand(currentEvent, name)
            }
            else {
                removeObjectBrand(currentEvent)
            }
        }
    }

    // Edit brand attributes
    const previousName = brand.name;
    const previousWeeklyShowName = brand.weeklyShowName;
    brand.name = name;
    brand.weeklyShowName = weeklyShowName;
    brand.weeklyShowIsExclusive = weeklyShowIsExclusive;
    brand.color = color;

    // Lower overness if name or weekly show name changed
    if (name !== previousName || previousWeeklyShowName !== weeklyShowName) {
        updateObjectOverness(brand, -10)
    }

    // Save to local storage
    await saveGameDataToLocalStorage(currentGame);

    return brand;
}

async function removeBrand(brandUid) {  // remove_brand_view
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const companyChampionships = await getCompanyChampionships(selectedCompany);
    const parentEvents = await getCompanyParentEvents(selectedCompany);
    const currentEvent = await getCompanyLatestEvent(selectedCompany);
    let rosterBrands = await getRosterBrands(mainRoster);

    // Get brand
    let brand = await Brands.get(brandUid);
    if (!brand) {
        data['error'] = 'brand-not-found';
        return data;
    }

    // Don't allow if at weekly show (can cause conflicts with brand index)
    if (currentEvent.weeklyShow || currentEvent.name === 'Weekly Show') {
        data['error'] = 'at-weekly-show';
        return data;
    }

    // Remove brand from roster
    await removeRosterBrands(mainRoster, [brand]);

    // Remove brand name from brand objects
    for (const x of [...rosterSuperstars, ...parentEvents, ...companyChampionships]) {
        if (x.brandName === brand.name) {
            removeObjectBrand(x)
        }
    }

    // Remove brand name from current event if added
    if (!currentEvent.weeklyShow) {
        if (currentEvent.brandName === brand.name) {
            removeObjectBrand(currentEvent)
        }
    }

    // Delete brand
    const brandName = brand.name;
    Brands.remove(brand);

    // Add company log
    const newCompanyLog = CompanyLog(currentGame.monthCount, selectedCompany.realYear);
    CompanyLogs.insert(newCompanyLog);
    newCompanyLog.text = brand.name;
    newCompanyLog.type = 'brand-removed';
    newCompanyLog.realYear = currentGame.year;
    await addCompanyCompanyLogs(selectedCompany, [newCompanyLog]);

    db.saveDatabase();

    // Get data to return
    data['brandName'] = brandName;
    data['currentEvent'] = {};
    data['currentEvent']['name'] = currentEvent.name;
    data['currentEvent']['brandName'] = currentEvent.brandName;
    data['currentEvent']['weeklyShow'] = currentEvent.weeklyShow;
    data['currentEvent']['brandColor'] = getBrandColorForEvent(currentEvent, rosterBrands);

    return data;
}

async function runBrandDraft(brandDraftSettings) {  // run_brand_draft
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const rosterBrands = await getRosterBrands(mainRoster);
    const cost = brandDraftSettings['type'] === 'full' ? 100 : 50;

    // Check if user is paid
    if (!globalUser.paid) {
        data['error'] = 'not-paid';
        return data;
    }

    // Check points
    if (selectedCompany.points < cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Get brands
    let brands = [];
    for (let brandUid of brandDraftSettings['brandUids']) {
        let brand = await Brands.get(brandUid);
        if (!brand || !objectInArray(brand, rosterBrands)) {
            data['error'] = 'brand-not-found';
            return data;
        }
        brands.push(brand);
    }

    // Check brands are present
    if (!brands.length) {
        data['error'] = 'no-brands-present';
        return data;
    }

    // Create draft event
    let draft = await createBrandDraft(selectedCompany, brands, brandDraftSettings['type'],
        (await getRosterSuperstars(mainRoster)));

    // Remove points
    selectedCompany.points -= cost;

    db.saveDatabase();

    // Get data to return
    data['draftData'] = await getDraftData(draft, selectedCompany);
    data['points'] = selectedCompany.points;

    return data;
}

async function saveYearlyBrandDraftSettings(yearlyBrandDraftSettings) {  // save_yearly_brand_draft_settings
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);

    // Update settings
    if (yearlyBrandDraftSettings['run']) {
        selectedCompany.settings['yearly_brand_draft_settings'] = yearlyBrandDraftSettings
    }
    else {
        selectedCompany.settings['yearly_brand_draft_settings'] = {}
    }

    db.saveDatabase();

    return data;
}

async function getBrandDraftSelectionsData() {  // get_run_brand_draft_selections
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const rosterBrands = await getRosterBrands(mainRoster);
    data['brands'] = rosterBrands.map(x => {
        return {'name': x.name, 'color': x.color, 'uid': x.$loki}
    });
    data['yearlyBrandDraftSettings'] = selectedCompany.settings['yearly_brand_draft_settings'];
    return data;
}

async function getBrandsDataForSegment() {  // get_brands_data_for_segment_view
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');
    const rosterBrands = await getRosterBrands(mainRoster);
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const rosterTagTeams = await getRosterTagTeams(mainRoster);

    // Get brands data
    data['brands'] = rosterBrands.map(x => {
        return {'name': x.name, 'uid': x.$loki}
    });

    // Get talent data
    data['talent'] = rosterSuperstars.filter(x => !x.fullTimeManager).map(x => {
        return {'name': x.name, 'uid': x.$loki, 'type': 'superstar'}
    });
    data['talent'].push(...(rosterTagTeams.filter(x => !x.temporary).map(x => {
        return {'name': x.name, 'uid': x.$loki, 'type': 'team'}
    })));
    data['talent'].sort((a, b) => a.name.localeCompare(b.name));

    return data;
}

function draftSuperstarToBrand(superstar, brand, companyChampionships) {  // draft_superstar_to_brand
    // Move superstar to brand
    superstar.brandName = brand;

    // Move superstar's championships to brand
    let superstarChampionships = companyChampionships.filter(x => x.superstarId === superstar.$loki);
    superstarChampionships = companyChampionships
        .filter(x => superstarChampionships.some(y => checkAreChampionshipsEqual(x, y)));  // Get team titles
    for (let championship of superstarChampionships) {
        championship.brandName = brand;
    }

    return superstar.brandName;
}

export {getViewBrandsData, getBrandColorForEvent, getBrandForEvent, getBrandIndexForWeeklyShow, setObjectBrand,
    removeObjectBrand, getAddBrandSelections, runBrandDraft, saveYearlyBrandDraftSettings, getBrandDraftSelectionsData,
    addOrEditBrand, removeBrand, getBrandsDataForSegment, draftSuperstarToBrand}